import React, { useState, useRef, useEffect } from 'react';
import { Button } from "@shared/ui/button";
import { ArrowRight, ArrowLeft, RefreshCw } from "lucide-react";
import { useParams } from 'react-router-dom';
import { useUtilityInfo, useGenerateOtp, useVerifyOtp } from '../../../hooks';
import { useTranslation } from 'react-i18next';
import { useToast } from "@shared/hooks/use-toast";

export const EnterOTP = ({ onNext, onPrevious }: any) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [otp, setOtp] = useState('');
  const [timer, setTimer] = useState(60);

  useEffect(() => {
    if (timer > 0) {
      const intervalId = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(intervalId);
    }
  }, [timer]);

  const { tenant, utilityName } = useParams<{ tenant?: string; utilityName?: string }>();
  const { data: utilityInfo } = useUtilityInfo(
    { tenant_alias: tenant || '', utility_alias: utilityName || '' },
    { enabled: !!tenant && !!utilityName }
  );

  const generateOtpMutation = useGenerateOtp();
  const verifyOtpMutation = useVerifyOtp();

  const identityType = sessionStorage.getItem('forgot_password_identity_type');
  const identityValue = sessionStorage.getItem('forgot_password_identity_value') || sessionStorage.getItem('forgot_password_email') || '';

  const handleResend = () => {
    const remoteUtilityId = utilityInfo?.result?.id || Number(localStorage.getItem('remoteUtilityId')) || 0;
    const payload: any = {
      remote_utility_id: remoteUtilityId
    };

    if (identityType === 'consumer_no') {
      payload.consumer_no = identityValue;
    } else {
      payload.email = identityValue;
    }

    generateOtpMutation.mutate(payload, {
      onSuccess: (res) => {
        const secretKey = 
          res?.otp?.result?.secret_key || 
          res?.result?.otp?.result?.secret_key || 
          res?.data?.otp?.result?.secret_key || 
          res?.secret_key;
          
        if (secretKey) {
          sessionStorage.setItem('forgot_password_secret_key', secretKey);
        } else {
          console.error("Could not find secret_key in resend response:", res);
        }
        setTimer(60);
      },
      onError: (err) => {
        console.error("Failed to resend OTP", err);
      }
    });
  };
  
  const handleVerify = () => {
    const secret_key = sessionStorage.getItem('forgot_password_secret_key') || '';
    const payload = {
      otp,
      secret_key
    };
    verifyOtpMutation.mutate(payload, {
      onSuccess: (res) => {
        console.log("OTP Verified", res);
        onNext();
      },
      onError: (err: any) => {
        console.error("OTP Verification Failed", err);
        const errorMessage = err?.response?.data?.message || err?.message || "Invalid OTP.";
        toast({
          title: "Verification Failed",
          description: errorMessage,
          variant: "destructive"
        });
      }
    });
  };

  return (
    <div className="flex flex-col p-2">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{t("Enter verification code")}</h2>
      <p className="text-gray-500 text-sm mb-6">
        {t("We sent a 6-digit code to ")}<span className="text-gray-800 font-medium">{identityValue}</span>
      </p>
      
      <div className="flex justify-center mb-8">
        <div className="w-full max-w-[320px]">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="Enter 6-digit code"
            className="w-full h-16 border-2 border-gray-200 rounded-2xl bg-gray-50/50 text-center text-2xl tracking-[0.25em] font-bold text-gray-900 focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-600 outline-none transition-all placeholder:text-gray-400 placeholder:font-medium placeholder:tracking-normal placeholder:text-lg shadow-sm"
          />
        </div>
      </div>

      {timer > 0 ? (
        <div className="text-center text-sm text-gray-500 mb-6 flex items-center justify-center gap-1">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          {t("Resend in ")}<span className="font-bold text-gray-800">{timer}s</span>
        </div>
      ) : (
        <div className="text-center mb-6">
          <button onClick={handleResend} className="text-sm text-blue-600 font-semibold hover:text-blue-700 flex items-center justify-center w-full">
            <RefreshCw className="w-4 h-4 mr-1" /> {t("Resend verification code")}
          </button>
        </div>
      )}

      <Button onClick={handleVerify} disabled={otp.length !== 6 || verifyOtpMutation.isPending} className="w-full bg-blue-400 hover:bg-blue-500 h-12 text-base mb-6 text-white border-0">
        {t("Verify Code")} <ArrowRight className="ml-2 h-4 w-4" />
      </Button>

      <div className="text-center">
        <button onClick={onPrevious} className="text-sm text-gray-500 hover:text-gray-800 flex items-center justify-center w-full">
          <ArrowLeft className="h-4 w-4 mr-2" /> {t("Use a different identifier")}
        </button>
      </div>
    </div>
  );
};
